/**
 * Перелёт карты к найденному судну (строка поиска в сайдбаре).
 *
 * Чистая часть: какой зум брать и что показать в карточке. Сам полёт — в
 * `useVesselFocus`, чтобы правила проверялись без карты.
 */

/** Судно, выбранное в строке поиска. */
export interface VesselFocus {
  mmsi: string
  name: string | null
  flag_state: string | null
  company: string | null
  lat: number
  lon: number
  last_position_utc: string | null
  /** Вид промысла судна, если его категория в реестре NPFC однозначна. */
  species_id?: number | null
  /** Реестр NPFC — как на карточке точки слоя флота: тип корпуса, орудие лова
   *  и цели промысла (имена видов обоими языками; словаря ASFIS у витрины нет). */
  rfmo_orgs?: string[] | null
  rfmo_type?: string | null
  rfmo_gear?: string | null
  rfmo_target_ru?: string[] | null
  rfmo_target_en?: string[] | null
  /** Ход и курс из последнего фикса АИС. */
  sog?: number | null
  cog?: number | null
}

/** Вид, на который переключить кабинет при выборе судна в поиске.
 *
 *  Владелец 2026-08-09: «когда категория судна однозначно связана с видом рыбы,
 *  автоматически изменять вид рыбы». Однозначность считает сервер (реестр NPFC →
 *  наш вид); фронту остаётся одна проверка — доступен ли вид ЭТОЙ организации.
 *  Переключись карта на закрытый для неё вид, его не было бы в выпадающем
 *  списке, и вернуться назад тем же путём человек не смог бы.
 *
 *  `null` — менять нечего: категории нет, она неоднозначна или вид недоступен. */
export function speciesForVessel(
  vessel: { species_id?: number | null },
  available: readonly number[],
): number | null {
  const id = vessel.species_id
  if (id == null) return null
  return available.includes(id) ? id : null
}

/** Зум, ближе которого судно уже различимо среди соседей. */
export const FOCUS_MIN_ZOOM = 8

/** Куда зумить: приближаем до рабочего, но НЕ отдаляем.
 *
 *  Человек мог сам подойти вплотную к району — сбрасывать его масштаб на «наш»
 *  значит отменять его работу. */
export function focusZoom(current: number): number {
  return Math.max(current, FOCUS_MIN_ZOOM)
}

/** Возраст позиции в часах — карточка показывает им «последняя позиция …».
 *  Без метки времени возраст неизвестен (null), а не «свежая». */
export function focusAgeHours(iso: string | null, now: number): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  return (now - t) / 3_600_000
}

/** Свойства карточки из результата поиска.
 *
 *  Имена полей — те же, что у свойств фичи слоя флота: карточка одна и та же,
 *  и расхождение имён показало бы прочерки на месте известных данных. */
export function focusCardProps(v: VesselFocus, now: number): Record<string, unknown> {
  const ageH = focusAgeHours(v.last_position_utc, now)
  return {
    mmsi: v.mmsi,
    name: v.name,
    flag_state: v.flag_state,
    company: v.company,
    rfmo_orgs: v.rfmo_orgs ?? null,
    rfmo_type: v.rfmo_type ?? null,
    rfmo_gear: v.rfmo_gear ?? null,
    rfmo_target_ru: v.rfmo_target_ru ?? null,
    rfmo_target_en: v.rfmo_target_en ?? null,
    sog: v.sog ?? null,
    cog: v.cog ?? null,
    last_position_utc: v.last_position_utc,
    age_h: ageH,
    // Порог тот же, что на карте (STALE_AGE_HOURS = 24 на бэкенде).
    stale: ageH != null && ageH > 24,
  }
}
